import React, { useMemo, useState } from "react";
import { FaCalendarAlt, FaLayerGroup, FaSearch, FaUsers } from "react-icons/fa";

import {
  getGroupAdherentsCount,
  getGroupName,
  getLocalDateInput,
  normalizeText,
} from "../utils/attendanceUtils";

function getGroupColorClasses(group) {
  const color = String(group?.color_groupe || "");

  if (color.includes("green")) {
    return "from-emerald-600 to-green-700";
  }

  if (color.includes("red")) {
    return "from-red-600 to-rose-700";
  }

  if (color.includes("yellow")) {
    return "from-amber-500 to-orange-600";
  }

  if (color.includes("purple")) {
    return "from-purple-600 to-indigo-700";
  }

  if (color.includes("pink")) {
    return "from-pink-600 to-rose-700";
  }

  return "from-sky-600 to-blue-700";
}

function AttendanceGroupPickerPanel({
  loading,
  groups = [],
  groupCounts = {},
  filters,
  setFilters,
  onSelectGroup,
}) {
  const [search, setSearch] = useState("");

  const filteredGroups = useMemo(() => {
    const query = normalizeText(search);

    return (groups || [])
      .filter((group) => {
        if (!query) return true;
        return normalizeText(getGroupName(group)).includes(query);
      })
      .slice()
      .sort((a, b) => getGroupName(a).localeCompare(getGroupName(b), "fr"));
  }, [groups, search]);

  return (
    <section className="rounded-[2rem] border border-white/80 bg-white/95 p-4 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/30 sm:p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
            <FaLayerGroup />
            Groupes coach
          </div>

          <h2 className="mt-3 font-titre text-xl font-bold text-slate-950 dark:text-white">
            Choisir le groupe du cours
          </h2>

          <p className="mt-2 text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
            Seuls les groupes rattachés à ton compte sont affichés.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-[180px_280px]">
          <label className="block">
            <span className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
              <FaCalendarAlt />
              Date
            </span>

            <input
              type="date"
              value={filters.selectedDate || getLocalDateInput()}
              disabled={loading}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  selectedDate: event.target.value,
                }))
              }
              className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 disabled:opacity-60 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
              Recherche
            </span>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nom du groupe..."
                className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
              />
            </div>
          </label>
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="h-28 animate-pulse rounded-[1.5rem] bg-slate-200 dark:bg-white/10"
              />
            ))}
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm font-bold text-slate-400 dark:border-white/10 dark:bg-slate-950">
            Aucun groupe rattaché à ton compte pour le moment.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredGroups.map((group) => {
              const adherentsCount = getGroupAdherentsCount(group, groupCounts);
              const gradient = getGroupColorClasses(group);

              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => onSelectGroup(group.id)}
                  className="group rounded-[1.75rem] border border-slate-200 bg-white p-4 text-left shadow-lg shadow-slate-200/50 transition hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-xl dark:border-white/10 dark:bg-slate-950 dark:shadow-black/20"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg shadow-slate-200 transition group-hover:scale-105 dark:shadow-black/30`}
                    >
                      <FaLayerGroup />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate font-titre text-lg font-bold text-slate-950 dark:text-white">
                        {getGroupName(group)}
                      </p>

                      <p className="mt-1 flex items-center gap-2 text-xs font-bold text-slate-400">
                        <FaUsers />
                        {adherentsCount} judoka
                        {adherentsCount > 1 ? "s" : ""}
                      </p>

                      <span className="mt-3 inline-flex min-h-9 items-center rounded-2xl bg-blue-50 px-3 text-xs font-bold text-blue-700 dark:bg-sky-500/10 dark:text-sky-200">
                        Commencer l’appel
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export default AttendanceGroupPickerPanel;